import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, FlaskConical } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { nbaToday, shortDate } from "@/lib/format";
import { TeamModel } from "@/components/season/TeamModel";
import { Backtest } from "@/components/season/Backtest";
import "@/components/season/SeasonModel.css";
import "@/components/season/SeasonModelArena.css";

const shift = (day, n) => { const d = new Date(`${day}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };
const tip = (iso) => new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });

function GameView({ day, teamsMap }) {
  const [games, setGames] = useState(null);
  const [sel, setSel] = useState("");
  const [data, setData] = useState(null);
  const [details, setDetails] = useState(false);
  useEffect(() => {
    setGames(null); setData(null);
    api.get("/season-model/games", { params: { day } }).then((r) => { setGames(r.data); setSel(r.data[0]?.id || ""); }).catch((e) => toast.error(errorText(e)));
  }, [day]);
  useEffect(() => {
    if (!sel) return;
    setData(null);
    api.get("/season-model/game", { params: { game_id: sel } }).then((r) => setData(r.data)).catch((e) => { setData({ error: errorText(e) }); });
  }, [sel]);
  if (!games) return <p className="smx-dim">Loading…</p>;
  if (!games.length) return <p className="smx-dim" data-testid="sm-no-games">No games on this day.</p>;
  return (
    <>
      <div className="smx-games" data-testid="sm-games">
        {games.map((g) => (
          <button key={g.id} type="button" className={`smx-game ${sel === g.id ? "is-active" : ""}`} onClick={() => setSel(g.id)} data-testid={`sm-game-${g.id}`}>
            <b>{g.away_code} @ {g.home_code}</b>
            <small>{g.status === "pre" ? tip(g.date_utc) : g.status === "post" ? "Final" : "Live"}{g.has_odds ? "" : " · no odds"}</small>
          </button>
        ))}
      </div>
      {!data ? <p className="smx-dim">Calculating…</p> : data.error ? <p className="smx-warn" data-testid="sm-error"><span>{data.error}</span></p> : (
        <div className="smx-teams">
          {data.teams.map((t, i) => {
            const other = data.teams[i === 0 ? 1 : 0];
            return <TeamModel key={t.team_id} t={t} meta={teamsMap[t.team_id]} oppCode={other?.team_code} home={i === 1} details={details} onToggleDetails={() => setDetails((d) => !d)} />;
          })}
        </div>
      )}
    </>
  );
}

export default function SeasonModel() {
  const [day, setDay] = useState(nbaToday());
  const [tab, setTab] = useState("game");
  const [report, setReport] = useState(null);
  const [running, setRunning] = useState(false);
  const [teamsMap, setTeamsMap] = useState({});
  const loadReport = useCallback(() => api.get("/season-model/backtest").then((r) => setReport(r.data)).catch(() => {}), []);
  useEffect(() => { loadReport(); }, [loadReport]);
  useEffect(() => {
    api.get("/teams").then((r) => setTeamsMap(Object.fromEntries(r.data.map((t) => [t.id, t])))).catch(() => {});
  }, []);
  const run = async () => {
    setRunning(true);
    try { const r = await api.post("/season-model/backtest"); setReport(r.data); toast.success("Backtest updated"); } catch (e) { toast.error(errorText(e)); }
    setRunning(false);
  };
  return (
    <div className="sm-root" data-testid="season-model">
      <header className="smx-head">
        <span className="smx-icon"><FlaskConical size={22} /></span>
        <div className="smx-head-main">
          <div className="smx-title">
            <h1>SEASON <span>MODEL</span></h1>
            <span className="smx-pill new" data-testid="sm-shadow-badge">NEW MODEL · SHADOW</span>
            <span className="smx-pill season">NBA 2026-27</span>
          </div>
          <p className="smx-sub">Player projections using season data, opponent difficulty, team rotations and advanced adjustments. Runs next to x4 and never changes it.</p>
        </div>
        <div className="smx-modes">
          <button type="button" className={tab === "game" ? "is-active" : ""} onClick={() => setTab("game")} data-testid="sm-tab-game">Games</button>
          <button type="button" className={tab === "backtest" ? "is-active" : ""} onClick={() => setTab("backtest")} data-testid="sm-tab-backtest">Backtest</button>
        </div>
      </header>
      {tab === "game" ? (
        <>
          <div className="smx-day">
            <button type="button" onClick={() => setDay(shift(day, -1))} aria-label="Previous day" data-testid="sm-prev-day"><ChevronLeft size={16} /></button>
            <span data-testid="sm-day">{shortDate(day)}</span>
            <button type="button" onClick={() => setDay(shift(day, 1))} aria-label="Next day" data-testid="sm-next-day"><ChevronRight size={16} /></button>
          </div>
          <GameView day={day} teamsMap={teamsMap} />
        </>
      ) : <Backtest report={report} running={running} onRun={run} />}
    </div>
  );
}
